import React from 'react';
import { Table } from 'reactstrap';

function formatTemps(temps) {
  if (!Array.isArray(temps) || temps.length === 0) return '—';
  return temps.map(t => (t?.value !== undefined ? `${Number(t.value).toFixed(2)}˚C` : '—')).join(' | ');
}

function batteryIcon(voltageRaw) {
  if (voltageRaw === undefined || voltageRaw === null || isNaN(Number(voltageRaw))) return null;
  let v = Number(voltageRaw);
  if (v > 20) v = v / 1000; // assume mV
  let level = 0;
  if (v > 4.0) level = 4; else if (v >= 3.8) level = 3; else if (v >= 3.6) level = 2; else level = 1;
  const color = level === 4 ? '#2ECC71' : level === 3 ? '#FFC107' : level === 2 ? '#FF9800' : '#F52C16';
  const blink = v < 3.4;
  return (
    <svg width="14" height="28" viewBox="0 0 14 28" xmlns="http://www.w3.org/2000/svg" aria-label={`${v.toFixed(2)}V`} title={`${v.toFixed(2)}V`}>
        <rect x="2" y="3" width="10" height="22" rx="2" ry="2" fill="none" stroke="#444" strokeWidth="1.5" />
        <rect x="5" y="1" width="4" height="3" rx="1" ry="1" fill="#444" />
        <g>
          {blink && <animate attributeName="opacity" values="1;0.2;1" dur="0.5s" repeatCount="indefinite" />} 
          {level >= 1 && <rect x="4" y="22" width="6" height="3" fill={color} />} 
          {level >= 2 && <rect x="4" y="18" width="6" height="3" fill={color} />} 
          {level >= 3 && <rect x="4" y="14" width="6" height="3" fill={color} />} 
          {level >= 4 && <rect x="4" y="10" width="6" height="3" fill={color} />} 
        </g>
      </svg>
  );
}

export default function TrucksTable({ stores, onVehicleSelect, selectedVehicle }) {
  const rows = Array.isArray(stores) ? stores : [];
  
  const handleRowClick = (vehicle) => {
    const vehiclePlate = vehicle?.vehicle_plate || '—';
    const deviceId = vehicle?.device_id;
    if (onVehicleSelect) {
      onVehicleSelect(deviceId, vehiclePlate);
    }
  };

  return (
    <Table size="sm" responsive style={{ textAlign: 'center' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'center' }}>Veículo</th>
          <th style={{ textAlign: 'center' }}>Temperatura</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((s) => {
          const vehiclePlate = s?.vehicle_plate || '—';
          const isSelected = selectedVehicle === vehiclePlate;
          const timestamp = s?.timestamp;
          const formatted = timestamp ? new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
          }).format(timestamp * 1000) : '—';
          const hasValidGps = Number.isFinite(s?.lat) && Number.isFinite(s?.long) && s.lat !== 0 && s.long !== 0;
          
          // Determine if vehicle is online (data less than 1 hour old)
          const currentTimestamp = Math.floor(Date.now() / 1000);
          const isOnline = s?.timestamp && (s.timestamp > (currentTimestamp - 60*60));
          const gpsColor = hasValidGps ? (isOnline ? '#2ECC71' : '#FFA500') : '#F52C16';
          const gpsTitle = hasValidGps ? (isOnline ? 'Online' : 'Offline (última posição)') : 'GPS ausente';
          
          return (
            <tr 
              key={vehiclePlate || Math.random()}
              onClick={() => handleRowClick(s)}
              style={{
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
              }}
            >
              <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <div style={{ fontWeight: isSelected ? 'bold' : 'normal' }}>{vehiclePlate}</div>
                    <div style={{ fontSize: '0.75em', color: '#666' }}>{formatted}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span title={gpsTitle} style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <svg width="22" height="22" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                        <path fill={gpsColor} d="M233 7c-9.4-9.4-24.6-9.4-33.9 0l-96 96c-9.4 9.4-9.4 24.6 0 33.9l89.4 89.4-15.5 15.5C152.3 230.4 124.9 224 96 224c-31.7 0-61.5 7.7-87.8 21.2c-9 4.7-10.3 16.7-3.1 23.8L112.7 376.7 96.3 393.1c-2.6-.7-5.4-1.1-8.3-1.1c-17.7 0-32 14.3-32 32s14.3 32 32 32s32-14.3 32-32c0-2.9-.4-5.6-1.1-8.3l16.4-16.4L242.9 506.9c7.2 7.2 19.2 5.9 23.8-3.1C280.3 477.5 288 447.7 288 416c0-28.9-6.4-56.3-17.8-80.9l15.5-15.5L375 409c9.4 9.4 24.6 9.4 33.9 0l96-96c9.4-9.4 9.4-24.6 0-33.9l-89.4-89.4 55-55c12.5-12.5 12.5-32.8 0-45.3l-48-48c-12.5-12.5-32.8-12.5-45.3 0l-55 55L233 7zm159 351l-72.4-72.4 62.1-62.1L454.1 296 392 358.1zM226.3 192.4L153.9 120 216 57.9l72.4 72.4-62.1 62.1z" />
                      </svg>
                    </span>
                    <div style={{ fontSize: '0.75em', color: '#666' }}>
                      {Number.isFinite(s?.velocity) ? `${Number(s.velocity).toFixed(1)} km/h` : '—'}
                    </div>
                  </div>
                </div>
              </td>
              <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                <div>{formatTemps(s?.temps)}</div>
              </td>
              <td style={{ verticalAlign: 'middle', textAlign: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{batteryIcon(s?.temps?.[0]?.voltage ?? s?.voltage)}</span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}